import React, { useMemo, useState } from 'react';
import {
  Button,
  Card,
  Col,
  Form,
  Row,
} from 'react-bootstrap';
import { Chart as ChartJS, registerables } from 'chart.js';
import { Chart } from 'react-chartjs-2';

ChartJS.register(...registerables);

type FilterPeriod = '7d' | '30d' | '90d';

const PERIOD_OPTIONS: { value: FilterPeriod; label: string }[] = [
  { value: '7d', label: 'Últimos 7 dias' },
  { value: '30d', label: 'Últimos 30 dias' },
  { value: '90d', label: 'Últimos 90 dias' },
];

interface DatasetPoint {
  label: string;
  sales: number;
  orders: number;
  conversion: number;
}

const SALES_DATA: Record<FilterPeriod, DatasetPoint[]> = {
  '7d': [
    { label: 'Seg', sales: 32000, orders: 38, conversion: 18 },
    { label: 'Ter', sales: 41000, orders: 45, conversion: 19 },
    { label: 'Qua', sales: 38000, orders: 40, conversion: 17 },
    { label: 'Qui', sales: 47000, orders: 52, conversion: 21 },
    { label: 'Sex', sales: 52000, orders: 57, conversion: 22 },
    { label: 'Sáb', sales: 36000, orders: 41, conversion: 16 },
    { label: 'Dom', sales: 30500, orders: 35, conversion: 14 },
  ],
  '30d': [
    { label: 'Semana 1', sales: 195000, orders: 212, conversion: 18 },
    { label: 'Semana 2', sales: 208000, orders: 230, conversion: 19 },
    { label: 'Semana 3', sales: 224000, orders: 246, conversion: 19 },
    { label: 'Semana 4', sales: 238000, orders: 262, conversion: 21 },
  ],
  '90d': [
    { label: 'Jan', sales: 602000, orders: 690, conversion: 16 },
    { label: 'Fev', sales: 658000, orders: 744, conversion: 17 },
    { label: 'Mar', sales: 702000, orders: 806, conversion: 18 },
  ],
};

const KPI_VARIANTS = [
  { key: 'sales', label: 'Receita total', format: (value: number) => `R$ ${(value / 1000).toFixed(1)}k` },
  { key: 'orders', label: 'Pedidos', format: (value: number) => value.toString() },
  { key: 'conversion', label: 'Conversão média', format: (value: number) => `${value.toFixed(1)}%` },
] as const;

export const SalesPerformanceCard: React.FC = () => {
  const [period, setPeriod] = useState<FilterPeriod>('30d');
  const [viewMode, setViewMode] = useState<'daily' | 'channel'>('daily');

  const dataset = SALES_DATA[period];

  const totals = useMemo(() => {
    const totalSales = dataset.reduce((acc, point) => acc + point.sales, 0);
    const totalOrders = dataset.reduce((acc, point) => acc + point.orders, 0);
    const avgConversion =
      dataset.reduce((acc, point) => acc + point.conversion, 0) / dataset.length;
    return {
      sales: totalSales,
      orders: totalOrders,
      conversion: avgConversion,
    };
  }, [dataset]);

  const chartData = useMemo(() => {
    if (viewMode === 'daily') {
      return {
        labels: dataset.map((point) => point.label),
        datasets: [
          {
            type: 'bar' as const,
            label: 'Receita (R$)',
            data: dataset.map((point) => point.sales),
            backgroundColor: 'rgba(13, 110, 253, 0.45)',
            borderColor: 'rgba(13, 110, 253, 0.9)',
            borderWidth: 1,
            yAxisID: 'y',
          },
          {
            type: 'line' as const,
            label: 'Conversão (%)',
            data: dataset.map((point) => point.conversion),
            borderColor: '#20c997',
            backgroundColor: '#20c997',
            tension: 0.4,
            yAxisID: 'y1',
          },
        ],
      };
    }

    const channelSeries = [
      { label: 'Direto', sales: 240000, conversion: 21 },
      { label: 'Orgânico', sales: 198000, conversion: 18 },
      { label: 'Social Ads', sales: 154000, conversion: 15 },
      { label: 'Parceiros', sales: 128000, conversion: 13 },
    ];

    return {
      labels: channelSeries.map((channel) => channel.label),
      datasets: [
        {
          type: 'bar' as const,
          label: 'Receita (R$)',
          data: channelSeries.map((channel) => channel.sales),
          backgroundColor: 'rgba(13, 110, 253, 0.45)',
          borderColor: 'rgba(13, 110, 253, 0.9)',
          borderWidth: 1,
          yAxisID: 'y',
        },
        {
          type: 'line' as const,
          label: 'Conversão (%)',
          data: channelSeries.map((channel) => channel.conversion),
          borderColor: '#20c997',
          backgroundColor: '#20c997',
          tension: 0.4,
          yAxisID: 'y1',
        },
      ],
    };
  }, [dataset, viewMode]);

  const chartOptions = useMemo(
    () => ({
      responsive: true,
      interaction: {
        mode: 'index' as const,
        intersect: false,
      },
      scales: {
        y: {
          type: 'linear' as const,
          display: true,
          position: 'left' as const,
          ticks: {
            callback: (value: string | number) => {
              if (typeof value === 'number') {
                return `R$ ${(value / 1000).toFixed(0)}k`;
              }
              return value;
            },
          },
        },
        y1: {
          type: 'linear' as const,
          display: true,
          position: 'right' as const,
          grid: {
            drawOnChartArea: false,
          },
          ticks: {
            callback: (value: string | number) => `${value}%`,
          },
        },
      },
      plugins: {
        legend: {
          display: true,
        },
      },
    }),
    [],
  );

  return (
    <Card className="shadow-sm border-0 h-100">
      <Card.Body>
        <div className="d-flex flex-column flex-lg-row justify-content-between gap-3 mb-4">
          <div>
            <Card.Title className="mb-1">Performance comercial</Card.Title>
            <Card.Subtitle className="text-muted">
              Visualize receita, pedidos e conversão em múltiplos recortes.
            </Card.Subtitle>
          </div>
          <div className="d-flex flex-wrap gap-2">
            <Form.Select
              size="sm"
              value={period}
              onChange={(event) => setPeriod(event.target.value as FilterPeriod)}
              style={{ width: '200px' }}
            >
              {PERIOD_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </Form.Select>
            <div className="btn-group btn-group-sm">
              <Button
                variant={viewMode === 'daily' ? 'primary' : 'outline-primary'}
                onClick={() => setViewMode('daily')}
              >
                Diário
              </Button>
              <Button
                variant={viewMode === 'channel' ? 'primary' : 'outline-primary'}
                onClick={() => setViewMode('channel')}
              >
                Por canal
              </Button>
            </div>
          </div>
        </div>

        <Row className="g-3 align-items-center mb-4">
          {KPI_VARIANTS.map((kpi) => (
            <Col md={4} key={kpi.key}>
              <Card className="border-0 bg-light">
                <Card.Body>
                  <div className="text-muted text-uppercase small mb-2">{kpi.label}</div>
                  <div className="fs-4 fw-semibold">
                    {kpi.format(totals[kpi.key])}
                  </div>
                </Card.Body>
              </Card>
            </Col>
          ))}
        </Row>

        <div className="analytics-chart">
          <Chart type="bar" data={chartData} options={chartOptions} />
        </div>
      </Card.Body>
    </Card>
  );
};
